/**
 * The JSON a report document is made of.
 *
 * A report's payload is genuinely open-ended: sections carry whatever shape
 * their kind needs, and new kinds are added without changing the envelope. So
 * it is JSON, and it is stored as JSON.
 *
 * "JSON" is not the same as "untyped", though, and this is the difference.
 * Annotating with `JsonValue` still rejects a `Date`, a `Map`, or a class
 * instance, any of which would pass an `any` and then fail silently on the way
 * to the server. Reading a nested value means narrowing at each step, which is
 * more verbose than indexing straight through, and that verbosity is the
 * point: it is where the assumption becomes visible.
 */

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue }

export type JsonObject = { [key: string]: JsonValue }

export type JsonArray = JsonValue[]

/** Whether a value is a JSON object, narrowing it so its fields can be read. */
export function isJsonObject(value: JsonValue | undefined): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Whether a value is a JSON array, narrowing it so it can be walked. */
export function isJsonArray(value: JsonValue | undefined): value is JsonArray {
  return Array.isArray(value)
}

/** A string field, or the fallback when it is absent or another type. */
export function readString(source: JsonValue | undefined, key: string, fallback = ''): string {
  if (!isJsonObject(source)) return fallback
  const value = source[key]
  return typeof value === 'string' ? value : fallback
}

/** A number field, or the fallback. Numeric strings are accepted and parsed. */
export function readNumber(source: JsonValue | undefined, key: string, fallback = 0): number {
  if (!isJsonObject(source)) return fallback
  const value = source[key]
  if (typeof value === 'number' && Number.isFinite(value)) return value
  // Amounts arrive as strings wherever precision had to survive the wire.
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return fallback
}

/** A boolean field, or the fallback. */
export function readBoolean(source: JsonValue | undefined, key: string, fallback = false): boolean {
  if (!isJsonObject(source)) return fallback
  const value = source[key]
  return typeof value === 'boolean' ? value : fallback
}

/** An array field, or an empty one. Never undefined, so callers can just walk it. */
export function readArray(source: JsonValue | undefined, key: string): JsonArray {
  if (!isJsonObject(source)) return []
  const value = source[key]
  return isJsonArray(value) ? value : []
}

/** An object field, or an empty one. */
export function readObject(source: JsonValue | undefined, key: string): JsonObject {
  if (!isJsonObject(source)) return {}
  const value = source[key]
  return isJsonObject(value) ? value : {}
}
